import type { SyntheticEvent } from 'react';
import { useCallback } from 'react';

import { rootUiActions } from 'app-components/flux/root-ui';
import { transactionsSelectors, transactionsTypes } from 'app-components/flux/transactions';
import { tracker } from 'app-components/utils/core';

import { getRootModalRemoveDialogOptions } from 'components/Drawers/standardDrawerContract';
import { investmentTransactionEditFormHooks } from 'components/Forms/InvestmentTransactionEditForm/investmentTransactionEditFormHooks';
import useTxnActions from 'data/transactionActions/useTxnActions';
import { useAppDispatch } from 'hooks/redux';
import trackEventTTI from 'utils/trackEventTTI';

import type { InvestmentTransactionEditViewProps } from '../types';

type CloseForm = (event?: SyntheticEvent, reason?: string) => void;

type EditControllerResult = {
  closeForm: CloseForm;
  deleteTransaction: () => void;
  isDirty: boolean;
  submitForm: () => void;
};

export function useInvestmentTransactionEditActions({
  context,
  dialogId,
  rootModalDrawer,
  transaction,
}: InvestmentTransactionEditViewProps): EditControllerResult {
  const dispatch = useAppDispatch();
  const isDirty = investmentTransactionEditFormHooks.useIsDirty();
  const manager = investmentTransactionEditFormHooks.useManager();
  const { deleteTransactions } = useTxnActions({ wrapperId: context });

  const closeForm = useCallback<CloseForm>(
    (_event, _reason) => {
      if (dialogId) {
        dispatch(rootUiActions.removeDialog(dialogId, getRootModalRemoveDialogOptions(rootModalDrawer)));
      }
    },
    [dialogId, dispatch, rootModalDrawer],
  );

  const deleteTransaction = useCallback(() => {
    const payload = transactionsTypes.mkInvestmentTransaction({
      ...transaction,
      isDeleted: true,
    });
    if (assert(payload?.id, 'transaction missing id', { payload, transaction })) {
      const trackingProps = transactionsSelectors.getTrackingProps(undefined, payload);
      trackEventTTI(tracker.events.txnDelete, trackingProps);
      deleteTransactions([payload]);
      closeForm();
    }
  }, [closeForm, deleteTransactions, transaction]);

  const submitForm = useCallback(() => void manager.submit(undefined), [manager]);

  return { closeForm, deleteTransaction, isDirty, submitForm };
}
